import { isDevMode } from '@angular/core';
import { CanActivateFn } from '@angular/router';

/**
 * Loads `PmaModule` before an EAGER route renders a component that PmaModule
 * declares.
 *
 * `/upload-coverage-proof` is routed with `component: PmaUploadCoverageProofComponent`
 * (an eager import in app-routing.module.ts), but that component — and the
 * `<upload-coverage>` / `<register-policy-form>` tree beneath it — is declared in
 * `PmaModule`, which is only ever loaded lazily by the `pma-tenant-onboarding`
 * routes. `AppModule` does not import it.
 *
 * Under AOT that is harmless: the compiler bakes each component's directives and
 * pipes into its own definition, which is why production hard-loads this route
 * correctly (verified by serving the production bundle).
 *
 * In development the scope is applied at runtime by `ɵɵsetNgModuleScope`, which
 * Angular emits behind an `ngDevMode` guard and which only runs when the module
 * file is EVALUATED. So unless something has already pulled in PmaModule's chunk,
 * the component renders with no module scope and dies with
 * `NG0302: The pipe 'async' could not be found`. That happens on a hard load, on a
 * refresh, and on the header menu's `router.navigateByUrl('/upload-coverage-proof')`
 * from any page where the PMA flow has not been visited yet — the page simply comes
 * up blank. It appears intermittent only because visiting the flow first warms the
 * chunk for the rest of the browser session.
 *
 * Awaiting the dynamic import here evaluates the module, which registers the scope,
 * before the component is created. Gated on `isDevMode()` so production keeps its
 * current behaviour exactly and pays nothing — no extra chunk request.
 *
 * The deeper inconsistency (an eager route for a lazily-declared component) is worth
 * fixing properly by moving these routes into PmaModule, but that needs real module
 * restructuring: PmaModule has no `exports` and its own `''` route already maps to
 * `PmaComponent`. See RESKIN_PROGRESS.md.
 */
export const pmaModuleScopeGuard: CanActivateFn = async () => {
  if (isDevMode()) {
    await import('../../pma/pma.module');
  }
  return true;
};
